import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { LinkedHazardsList } from './LinkedHazardsList'
import { LinkedRrmsList } from './LinkedRrmsList'
import type { EhsrOverviewNode } from './buildEhsrOverviewModel'

function CoverageBadges({ node }: { node: EhsrOverviewNode }) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const hasHazards = node.hazardCoverage.covered > 0
  const hasRrms = node.rrmCoverage.covered > 0

  return (
    <div className='flex shrink-0 items-center gap-2'>
      <Badge
        variant='outline'
        className={cn(
          'font-normal',
          hasHazards && 'border-amber-300 bg-amber-50 text-amber-800',
          !hasHazards && 'text-muted-foreground'
        )}
      >
        {t('ehsrsOverviewPage.coverage.hazards', {
          covered: node.hazardCoverage.covered,
        })}
      </Badge>
      <Badge
        variant='outline'
        className={cn(
          'font-normal',
          hasRrms && 'border-primary/30 bg-primary/10 text-primary',
          !hasRrms && 'text-muted-foreground'
        )}
      >
        {t('ehsrsOverviewPage.coverage.rrms', {
          covered: node.rrmCoverage.covered,
        })}
      </Badge>
    </div>
  )
}

function getPaddingClass(depth: number) {
  if (depth === 0) {
    return ''
  }
  if (depth === 1) {
    return 'ps-2'
  }
  if (depth === 2) {
    return 'ps-3'
  }
  return 'ps-4'
}

export function EhsrOverviewNodeCard({
  node,
  depth = 0,
}: {
  node: EhsrOverviewNode
  depth?: number
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [open, setOpen] = useState(false)

  const paddingClass = getPaddingClass(depth)

  const hasLinkedItems =
    node.linkedHazards.length > 0 || node.linkedRrms.length > 0
  const showEmptyState = node.kind === 'leaf' && !hasLinkedItems

  let headingLabel = node.title
  if (node.kind === 'leaf' && node.selectedLabel != null) {
    headingLabel = node.selectedLabel
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={paddingClass}>
      <div
        className={cn(
          'overflow-hidden',
          depth > 0 && 'border-dashed shadow-none'
        )}
      >
        <div>
          <CollapsibleTrigger
            render={
              <button
                type='button'
                className={cn(
                  'flex w-full items-start rounded-md p-3 text-start hover:bg-muted/40',
                  open && 'bg-muted/40',
                  node.kind === 'leaf' &&
                    open &&
                    'border border-border bg-muted/60 hover:bg-muted/70'
                )}
              />
            }
          >
            <div className='flex min-w-0 flex-1 items-start'>
              <div className='flex max-w-full items-start gap-2'>
                <span className='line-clamp-2 min-w-0 text-sm font-medium text-foreground'>
                  {headingLabel}
                </span>
                <CoverageBadges node={node} />
              </div>
            </div>
            <ChevronRight
              className={cn(
                'ms-auto mt-0.5 size-4 shrink-0 transition-transform duration-200',
                open && 'rotate-90'
              )}
              aria-hidden
            />
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent>
          <div>
            {node.kind === 'leaf' && (
              <div className='space-y-4 rounded-md bg-muted/10 p-3'>
                {showEmptyState && (
                  <p className='text-sm text-muted-foreground'>
                    {t('ehsrsOverviewPage.emptyClause')}
                  </p>
                )}
                <LinkedHazardsList hazards={node.linkedHazards} />
                <LinkedRrmsList measures={node.linkedRrms} />
              </div>
            )}

            {node.children.length > 0 && (
              <div
                className={cn(
                  'space-y-3',
                  open &&
                    'ms-3.5 translate-x-px border-s border-muted ps-2.5 pt-1 pb-1'
                )}
              >
                {node.children.map((child) => (
                  <EhsrOverviewNodeCard
                    key={child.nodeId}
                    node={child}
                    depth={depth + 1}
                  />
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
